import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  Textarea,
  TextFilter,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { fetchTenants, createTenant, setTenantArchived } from '../api/tenants';
import { HealthDot } from '../components/HealthDot';
import { StatusBadge } from '../components/StatusBadge';
import type { Tenant, TenantSummary, CreateTenantInput } from '../types/tenant';

function SummaryCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <Container>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="awsui-value-large">
        <span style={{ color: accent ?? '#002D43', fontWeight: 700, fontSize: 32 }}>{value}</span>
      </Box>
    </Container>
  );
}

const TIER_OPTIONS: SelectProps.Options = [
  { label: 'Assessment', value: 'assessment', description: 'DISCOVER / WAFR / FTR — gap assessment & evaluation' },
  { label: 'Build', value: 'build', description: 'Standalone project delivery (independent engagement)' },
  { label: 'LaunchLogic', value: 'launchlogic', description: 'Post-BUILD transition & stabilization (includes BUILD)' },
  { label: 'Managed Digital', value: 'managed-digital', description: 'SMB managed digital transformation (includes BUILD)' },
  { label: 'CSP', value: 'csp', description: 'Ongoing managed cloud services — after LL, MD, or direct' },
];

const REGION_OPTIONS: SelectProps.Options = [
  { label: 'US East (N. Virginia)', value: 'us-east-1' },
  { label: 'US West (Oregon)', value: 'us-west-2' },
  { label: 'EU (Ireland)', value: 'eu-west-1' },
  { label: 'EU (Frankfurt)', value: 'eu-central-1' },
  { label: 'AP (Sydney)', value: 'ap-southeast-2' },
];

const ACCOUNT_MODEL_OPTIONS: SelectProps.Options = [
  { label: 'SPAM — g/d/n/a Owned', value: 'spam', description: 'We own the AWS account, single invoice, +7% reseller margin' },
  { label: 'ECAM — Customer Owned', value: 'ecam', description: 'Customer owns AWS account, we access via cross-account roles' },
];

const COLUMN_DEFINITIONS: TableProps.ColumnDefinition<Tenant>[] = [
  { id: 'name', header: 'Tenant', cell: (item) => item.name, sortingField: 'name', width: 180 },
  { id: 'company', header: 'Company', cell: (item) => item.companyName, sortingField: 'companyName' },
  { id: 'status', header: 'Status', cell: (item) => <StatusBadge status={item.status} />, sortingField: 'status', width: 130 },
  {
    id: 'products', header: 'Products', width: 180,
    cell: (item) => {
      const count = item.products?.length || 0;
      if (count === 0) return '—';
      return item.products.map((p) => p.productName).join(', ');
    },
  },
  {
    id: 'deployStatus', header: 'Deploy Status', width: 130,
    cell: (item) => {
      const first = item.products?.[0];
      if (!first) return '—';
      const colors: Record<string, string> = {
        pending: '#ACB7C5', deploying: '#0096FF', deployed: '#22C55E', failed: '#EF4444', updating: '#0096FF',
      };
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[first.status] ?? '#ACB7C5', display: 'inline-block' }} />
          {first.status.replace('_', ' ')}
        </span>
      );
    },
  },
  { id: 'account', header: 'AWS Account', cell: (item) => item.awsAccountId || '—', width: 130 },
  {
    id: 'hosting', header: 'Hosting', width: 100,
    cell: (item) => {
      const mode = item.products?.find((p) => p.hostingMode)?.hostingMode;
      if (!mode) return '—';
      return mode === 'saas-hosted' ? '☁️ SaaS' : '🏗️ Self';
    },
  },
  { id: 'region', header: 'Region', cell: (item) => item.region, width: 110 },
];

interface ProvisionForm {
  name: string;
  companyName: string;
  contactEmail: string;
  awsAccountId: string;
  region: SelectProps.Option | null;
  subscriptionTier: SelectProps.Option | null;
  accountModel: SelectProps.Option | null;
  notes: string;
}

const EMPTY_FORM: ProvisionForm = {
  name: '', companyName: '', contactEmail: '', awsAccountId: '',
  region: { label: 'US East (N. Virginia)', value: 'us-east-1' },
  subscriptionTier: { label: 'Assessment', value: 'assessment' },
  accountModel: { label: 'ECAM — Customer Owned', value: 'ecam' },
  notes: '',
};

function validateAwsAccountId(id: string): string | null {
  if (!id) return null; // Optional — may not have it at assessment stage
  const cleaned = id.replace(/[\s-]/g, '');
  if (!/^\d{12}$/.test(cleaned)) return 'Must be exactly 12 digits (or leave blank)';
  return null;
}

function validateEmail(email: string): string | null {
  if (!email) return 'Contact email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Invalid email format';
  return null;
}

export function TenantDashboard() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [summary, setSummary] = useState<TenantSummary>({
    total: 0, active: 0, provisioning: 0, suspended: 0, archived: 0, healthy: 0, degraded: 0, unhealthy: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<Tenant[]>([]);

  // Faceting + archive view
  const [statusFacet, setStatusFacet] = useState<SelectProps.Option>({ label: 'Any status', value: '' });
  const [tierFacet, setTierFacet] = useState<SelectProps.Option>({ label: 'Any engagement', value: '' });
  const [productFacet, setProductFacet] = useState<SelectProps.Option>({ label: 'Any product', value: '' });
  const [hostingFacet, setHostingFacet] = useState<SelectProps.Option>({ label: 'Any hosting', value: '' });
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Provision modal state
  const [showProvision, setShowProvision] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState<ProvisionForm>({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState<Record<string, string | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const PAGE_SIZE = 20;

  useEffect(() => { loadTenants(); }, []);

  async function loadTenants() {
    setLoading(true);
    try {
      const data = await fetchTenants();
      setTenants(data.tenants);
      setSummary(data.summary);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  }

  function openProvisionModal() {
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setSubmitError(null);
    setConfirmChecked(false);
    setShowConfirm(false);
    setShowProvision(true);
  }

  function validateForm(): boolean {
    const errors: Record<string, string | null> = {};
    if (!form.name.trim()) errors.name = 'Tenant name is required';
    if (!form.companyName.trim()) errors.companyName = 'Company name is required';
    errors.contactEmail = validateEmail(form.contactEmail);
    errors.awsAccountId = validateAwsAccountId(form.awsAccountId);
    if (!form.region?.value) errors.region = 'Region is required';
    if (!form.subscriptionTier?.value) errors.subscriptionTier = 'Subscription tier is required';

    setFormErrors(errors);
    return Object.values(errors).every((e) => e === null);
  }

  function handleReviewProvision() {
    if (validateForm()) {
      setSubmitError(null);
      setShowConfirm(true);
    }
  }

  async function handleProvision() {
    if (!confirmChecked) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input: CreateTenantInput = {
        name: form.name.trim(),
        companyName: form.companyName.trim(),
        contactEmail: form.contactEmail.trim(),
        awsAccountId: form.awsAccountId.trim(),
        region: form.region?.value || 'us-east-1',
        subscriptionTier: (form.subscriptionTier?.value || 'assessment') as CreateTenantInput['subscriptionTier'],
        accountModel: (form.accountModel?.value || 'ecam') as 'spam' | 'ecam',
        notes: form.notes.trim() || undefined,
      };
      const newTenant = await createTenant(input);
      setShowProvision(false);
      setShowConfirm(false);
      setSubmitting(false);
      navigate(`/ops/tenants/${newTenant.tenantId}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to provision tenant');
      setSubmitting(false);
    }
  }

  function updateField<K extends keyof ProvisionForm>(field: K, value: ProvisionForm[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error on edit
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  }

  async function handleArchiveToggle(tenant: Tenant) {
    const nextArchived = tenant.status !== 'archived';
    setArchiving(true);
    try {
      await setTenantArchived(tenant.tenantId, nextArchived);
      setSelectedItems([]);
      await loadTenants();
    } catch (err) {
      console.error('Failed to archive tenant:', err);
    } finally {
      setArchiving(false);
    }
  }

  // Build product facet options from what's actually deployed across tenants.
  const productOptions: SelectProps.Options = [
    { label: 'Any product', value: '' },
    ...Array.from(
      new Map(
        tenants.flatMap((t) => t.products ?? []).map((p) => [p.productId, p.productName]),
      ).entries(),
    ).map(([value, label]) => ({ label, value })),
  ];

  const STATUS_FACET_OPTIONS: SelectProps.Options = [
    { label: 'Any status', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Provisioning', value: 'provisioning' },
    { label: 'Suspended', value: 'suspended' },
    { label: 'Deprovisioning', value: 'deprovisioning' },
    { label: 'Deprovisioned', value: 'deprovisioned' },
    { label: 'Archived', value: 'archived' },
  ];

  const TIER_FACET_OPTIONS: SelectProps.Options = [
    { label: 'Any engagement', value: '' },
    ...TIER_OPTIONS,
  ];

  const HOSTING_FACET_OPTIONS: SelectProps.Options = [
    { label: 'Any hosting', value: '' },
    { label: '☁️ SaaS Hosted', value: 'saas-hosted' },
    { label: '🏗️ Self-Deployed', value: 'self-deployed' },
  ];

  const filteredTenants = tenants.filter((t) => {
    // Archived tenants are hidden unless the operator opts in, or is explicitly
    // faceting on the archived status.
    if (t.status === 'archived' && !showArchived && statusFacet.value !== 'archived') return false;

    if (statusFacet.value && t.status !== statusFacet.value) return false;
    if (tierFacet.value && t.subscriptionTier !== tierFacet.value) return false;
    if (productFacet.value && !(t.products ?? []).some((p) => p.productId === productFacet.value)) return false;
    if (hostingFacet.value && !(t.products ?? []).some((p) => p.hostingMode === hostingFacet.value)) return false;

    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      t.name.toLowerCase().includes(search) ||
      t.companyName.toLowerCase().includes(search) ||
      t.contactEmail.toLowerCase().includes(search) ||
      t.status.includes(search)
    );
  });

  const paginatedTenants = filteredTenants.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadTenants} loading={loading}>Refresh</Button>
            <Button variant="primary" onClick={openProvisionModal}>Provision Tenant</Button>
          </SpaceBetween>
        }
      >
        Tenants
      </Header>

      <ColumnLayout columns={4} variant="text-grid">
        <SummaryCard label="Total Tenants" value={summary.total} />
        <SummaryCard label="Active" value={summary.active} accent="#22C55E" />
        <SummaryCard label="Provisioning" value={summary.provisioning} accent="#0096FF" />
        <SummaryCard label="Archived" value={summary.archived} accent="#ACB7C5" />
      </ColumnLayout>

      <Table
        columnDefinitions={COLUMN_DEFINITIONS}
        items={paginatedTenants}
        loading={loading}
        loadingText="Loading tenants..."
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        onRowClick={({ detail }) => navigate(`/ops/tenants/${detail.item.tenantId}`)}
        trackBy="tenantId"
        variant="full-page"
        stickyHeader
        stripedRows
        empty={
          <Box textAlign="center" color="inherit">
            <b>No tenants</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No tenants provisioned yet. Click "Provision Tenant" to get started.
            </Box>
            <Button variant="primary" onClick={openProvisionModal}>Provision Tenant</Button>
          </Box>
        }
        filter={
          <SpaceBetween size="xs">
            <TextFilter
              filteringText={filterText}
              filteringPlaceholder="Search tenants..."
              onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(1); }}
            />
            <SpaceBetween direction="horizontal" size="xs">
              <Select
                selectedOption={statusFacet}
                options={STATUS_FACET_OPTIONS}
                onChange={({ detail }) => { setStatusFacet(detail.selectedOption); setCurrentPage(1); }}
              />
              <Select
                selectedOption={tierFacet}
                options={TIER_FACET_OPTIONS}
                onChange={({ detail }) => { setTierFacet(detail.selectedOption); setCurrentPage(1); }}
              />
              <Select
                selectedOption={productFacet}
                options={productOptions}
                onChange={({ detail }) => { setProductFacet(detail.selectedOption); setCurrentPage(1); }}
              />
              <Select
                selectedOption={hostingFacet}
                options={HOSTING_FACET_OPTIONS}
                onChange={({ detail }) => { setHostingFacet(detail.selectedOption); setCurrentPage(1); }}
              />
              <Checkbox checked={showArchived} onChange={({ detail }) => { setShowArchived(detail.checked); setCurrentPage(1); }}>
                Show archived
              </Checkbox>
            </SpaceBetween>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={Math.ceil(filteredTenants.length / PAGE_SIZE) || 1}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
        }
        header={
          <Header
            counter={`(${filteredTenants.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={() => { if (selectedItems[0]) navigate(`/ops/tenants/${selectedItems[0].tenantId}`); }}
                >
                  View Details
                </Button>
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={() => { if (selectedItems[0]) navigate(`/ops/deployments/${selectedItems[0].tenantId}`); }}
                >
                  Role Deployments
                </Button>
                <Button
                  disabled={selectedItems.length === 0}
                  loading={archiving}
                  iconName={selectedItems[0]?.status === 'archived' ? 'undo' : 'close'}
                  onClick={() => { if (selectedItems[0]) handleArchiveToggle(selectedItems[0]); }}
                >
                  {selectedItems[0]?.status === 'archived' ? 'Unarchive' : 'Archive'}
                </Button>
                <Button
                  variant="primary"
                  disabled={selectedItems.length === 0}
                  onClick={() => { if (selectedItems[0]) navigate(`/ops/deployments/${selectedItems[0].tenantId}`); }}
                >
                  Deploy Roles →
                </Button>
              </SpaceBetween>
            }
          >
            All Tenants
          </Header>
        }
      />

      {/* ── Provision Tenant Modal ── */}
      <Modal
        visible={showProvision && !showConfirm}
        onDismiss={() => setShowProvision(false)}
        header="Provision New Tenant"
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowProvision(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleReviewProvision}>Review &amp; Confirm</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info">
            This will create a new tenant record in the Peregrine control plane. After provisioning,
            you can deploy cross-account trust roles from the Role Deployments page.
          </Alert>

          <ColumnLayout columns={2}>
            <FormField label="Tenant Name" errorText={formErrors.name} constraintText="Required — display name for this tenant">
              <Input value={form.name} onChange={({ detail }) => updateField('name', detail.value)} placeholder="e.g., Acme Corp" />
            </FormField>
            <FormField label="Company Name" errorText={formErrors.companyName} constraintText="Required — legal entity name">
              <Input value={form.companyName} onChange={({ detail }) => updateField('companyName', detail.value)} placeholder="e.g., Acme Corporation Inc." />
            </FormField>
          </ColumnLayout>

          <ColumnLayout columns={2}>
            <FormField label="Contact Email" errorText={formErrors.contactEmail} constraintText="Required — primary contact for this tenant">
              <Input value={form.contactEmail} onChange={({ detail }) => updateField('contactEmail', detail.value)} placeholder="admin@acme.com" type="email" />
            </FormField>
            <FormField label="AWS Account ID" errorText={formErrors.awsAccountId} constraintText="Optional — 12-digit customer AWS account (can add later)">
              <Input value={form.awsAccountId} onChange={({ detail }) => updateField('awsAccountId', detail.value)} placeholder="123456789012" inputMode="numeric" />
            </FormField>
          </ColumnLayout>

          <ColumnLayout columns={2}>
            <FormField label="Region" errorText={formErrors.region}>
              <Select selectedOption={form.region} onChange={({ detail }) => updateField('region', detail.selectedOption)} options={REGION_OPTIONS} />
            </FormField>
            <FormField label="Engagement Type" errorText={formErrors.subscriptionTier}>
              <Select selectedOption={form.subscriptionTier} onChange={({ detail }) => updateField('subscriptionTier', detail.selectedOption)} options={TIER_OPTIONS} />
            </FormField>
          </ColumnLayout>

          <ColumnLayout columns={2}>
            <FormField label="Account Model" description="Who owns the AWS account?">
              <Select selectedOption={form.accountModel} onChange={({ detail }) => updateField('accountModel', detail.selectedOption)} options={ACCOUNT_MODEL_OPTIONS} />
            </FormField>
            <div />
          </ColumnLayout>

          <FormField label="Notes" constraintText="Optional — internal notes about this tenant">
            <Textarea value={form.notes} onChange={({ detail }) => updateField('notes', detail.value)} rows={3} placeholder="e.g., Referred by Kyle, FTR candidate, needs Archera + PC3" />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* ── Confirmation Modal ── */}
      <Modal
        visible={showConfirm}
        onDismiss={() => setShowConfirm(false)}
        header="Confirm Tenant Provisioning"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowConfirm(false)}>Back</Button>
              <Button variant="primary" onClick={handleProvision} loading={submitting} disabled={!confirmChecked}>
                Provision Tenant
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {submitError && <Alert type="error">{submitError}</Alert>}

          <Alert type="warning">
            You are about to create a new tenant record. This action will be logged for SOC 2 audit compliance.
            Please verify all details below are correct.
          </Alert>

          <Container header={<Header variant="h3">Tenant Details</Header>}>
            <ColumnLayout columns={2}>
              <div><Box variant="awsui-key-label">Tenant Name</Box><Box>{form.name}</Box></div>
              <div><Box variant="awsui-key-label">Company</Box><Box>{form.companyName}</Box></div>
              <div><Box variant="awsui-key-label">Contact Email</Box><Box>{form.contactEmail}</Box></div>
              <div><Box variant="awsui-key-label">AWS Account ID</Box><Box><code>{form.awsAccountId}</code></Box></div>
              <div><Box variant="awsui-key-label">Region</Box><Box>{form.region?.label}</Box></div>
              <div><Box variant="awsui-key-label">Engagement Type</Box><Box>{form.subscriptionTier?.label}</Box></div>
              <div><Box variant="awsui-key-label">Account Model</Box><Box>{form.accountModel?.label}</Box></div>
            </ColumnLayout>
            {form.notes && (
              <div style={{ marginTop: 12 }}><Box variant="awsui-key-label">Notes</Box><Box>{form.notes}</Box></div>
            )}
          </Container>

          <Checkbox checked={confirmChecked} onChange={({ detail }) => setConfirmChecked(detail.checked)}>
            I confirm this tenant information is correct and authorize provisioning. I understand this action is audited.
          </Checkbox>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
